/**
 * Strips all non-digit characters from a string.
 */
function stripNonDigits(value: string): string {
  return value.replace(/\D/g, '')
}

/**
 * Normalizes a phone string to 10 digits.
 * Strips leading "1" from 11-digit numbers.
 * Returns null if the result isn't 10 digits.
 */
function normalizeTo10(value: string): string | null {
  let digits = stripNonDigits(value)
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }
  return digits.length === 10 ? digits : null
}

/**
 * Formats a phone number for display: (XXX) XXX-XXXX
 * Returns the original string if it can't be parsed as a 10-digit US number.
 */
export function formatPhoneNumber(value: string | undefined | null): string {
  if (!value) return ''
  const digits = normalizeTo10(value)
  if (!digits) return value
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/**
 * Formats phone input as the user types, progressively building (XXX) XXX-XXXX.
 * Only allows digits, limits to 10 digits max. A leading country-code "1" on an
 * 11-digit number is dropped, since numbers are stored as 1XXXXXXXXXX (see toE164)
 * and would otherwise be cut off at the wrong end.
 */
export function formatPhoneInput(value: string): string {
  let digits = stripNonDigits(value)
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  digits = digits.slice(0, 10)
  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/**
 * Validates that a value contains exactly 10 digits (after stripping formatting).
 */
export function isValidPhone(value: string | undefined | null): boolean {
  if (!value) return false
  return normalizeTo10(value) !== null
}

/**
 * Converts a phone number to backend storage format: 1XXXXXXXXXX
 * Returns the original string if it can't be parsed.
 */
export function toE164(value: string | undefined | null): string {
  if (!value) return ''
  const digits = normalizeTo10(value)
  if (!digits) return value
  return `1${digits}`
}
